'use client';

import React, { useState, useRef } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { HiOutlineCurrencyDollar } from '@react-icons/all-files/hi/HiOutlineCurrencyDollar';
import { HiOutlineTrendingDown } from '@react-icons/all-files/hi/HiOutlineTrendingDown';
import { HiOutlineChartBar } from '@react-icons/all-files/hi/HiOutlineChartBar';
import {
  StyledToolHero,
  StyledToolBody,
  StyledCalculator,
  StyledFieldsGrid,
  StyledFormGroup,
  StyledCalculateButton,
  StyledResult,
  StyledAllocationGrid,
  StyledAllocationCard,
  StyledWidgetsSection,
  StyledWidgetGrid,
  StyledWidgetCard,
  StyledRelatedTools,
  StyledRelatedToolGrid,
  StyledRelatedToolCard,
} from './MarketingBudgetTool.styles';

interface IWidget {
  _id: string;
  name: string;
  slug: string;
  logo: string;
  teaser: string;
}

interface IRelatedTool {
  slug: string;
  name: string;
  description: string;
  icon?: string;
}

interface IChannelAllocation {
  channel: string;
  percentage: number;
  amount: number;
}

interface IToolResult {
  totalBudget: number;
  businessType: string;
  goal: string;
  allocations: IChannelAllocation[];
  status: 'good';
}

interface IProps {
  recommendedWidgets: IWidget[];
  relatedTools: IRelatedTool[];
}

const TOOL_ICONS: Record<string, React.ReactNode> = {
  'cac-calculator': <HiOutlineCurrencyDollar />,
  'churn-calculator': <HiOutlineTrendingDown />,
  'marketing-budget-allocator': <HiOutlineChartBar />,
};

type BusinessType = 'b2b-saas' | 'b2c-ecommerce' | 'b2b-services' | 'd2c-brand' | 'local-business';
type GoalType = 'brand-awareness' | 'lead-generation' | 'sales-revenue' | 'customer-retention';

const ALLOCATION_MAP: Record<BusinessType, Record<GoalType, Record<string, number>>> = {
  'b2b-saas': {
    'brand-awareness': { 'Content Marketing': 25, 'Social Ads': 20, 'SEO': 20, 'Paid Search': 10, 'Events/PR': 15, 'Influencer/Partnerships': 5, 'Email Marketing': 5 },
    'lead-generation': { 'Content Marketing': 25, 'Paid Search': 25, 'SEO': 20, 'Email Marketing': 15, 'Social Ads': 10, 'Events/PR': 5, 'Influencer/Partnerships': 0 },
    'sales-revenue': { 'Paid Search': 30, 'Email Marketing': 20, 'Content Marketing': 15, 'SEO': 15, 'Social Ads': 10, 'Events/PR': 5, 'Influencer/Partnerships': 5 },
    'customer-retention': { 'Email Marketing': 30, 'Content Marketing': 25, 'Social Ads': 15, 'Events/PR': 10, 'SEO': 10, 'Paid Search': 5, 'Influencer/Partnerships': 5 },
  },
  'b2c-ecommerce': {
    'brand-awareness': { 'Social Ads': 30, 'Influencer/Partnerships': 20, 'Content Marketing': 15, 'SEO': 15, 'Paid Search': 10, 'Email Marketing': 5, 'Events/PR': 5 },
    'lead-generation': { 'Social Ads': 25, 'Paid Search': 20, 'Email Marketing': 20, 'SEO': 15, 'Content Marketing': 10, 'Influencer/Partnerships': 5, 'Events/PR': 5 },
    'sales-revenue': { 'Paid Search': 25, 'Social Ads': 25, 'Email Marketing': 20, 'SEO': 10, 'Influencer/Partnerships': 10, 'Content Marketing': 5, 'Events/PR': 5 },
    'customer-retention': { 'Email Marketing': 35, 'Social Ads': 20, 'Content Marketing': 15, 'Influencer/Partnerships': 10, 'SEO': 10, 'Paid Search': 5, 'Events/PR': 5 },
  },
  'b2b-services': {
    'brand-awareness': { 'Content Marketing': 25, 'SEO': 20, 'Events/PR': 20, 'Social Ads': 15, 'Paid Search': 10, 'Email Marketing': 5, 'Influencer/Partnerships': 5 },
    'lead-generation': { 'Paid Search': 25, 'Content Marketing': 20, 'SEO': 20, 'Email Marketing': 15, 'Events/PR': 10, 'Social Ads': 5, 'Influencer/Partnerships': 5 },
    'sales-revenue': { 'Paid Search': 25, 'Email Marketing': 25, 'Content Marketing': 15, 'SEO': 15, 'Events/PR': 10, 'Social Ads': 5, 'Influencer/Partnerships': 5 },
    'customer-retention': { 'Email Marketing': 30, 'Content Marketing': 20, 'Events/PR': 20, 'SEO': 10, 'Social Ads': 10, 'Paid Search': 5, 'Influencer/Partnerships': 5 },
  },
  'd2c-brand': {
    'brand-awareness': { 'Social Ads': 30, 'Influencer/Partnerships': 25, 'Content Marketing': 15, 'SEO': 10, 'Paid Search': 10, 'Email Marketing': 5, 'Events/PR': 5 },
    'lead-generation': { 'Social Ads': 25, 'Paid Search': 20, 'Email Marketing': 20, 'Influencer/Partnerships': 15, 'Content Marketing': 10, 'SEO': 5, 'Events/PR': 5 },
    'sales-revenue': { 'Social Ads': 25, 'Paid Search': 25, 'Email Marketing': 20, 'Influencer/Partnerships': 15, 'Content Marketing': 5, 'SEO': 5, 'Events/PR': 5 },
    'customer-retention': { 'Email Marketing': 30, 'Social Ads': 25, 'Influencer/Partnerships': 15, 'Content Marketing': 15, 'SEO': 5, 'Paid Search': 5, 'Events/PR': 5 },
  },
  'local-business': {
    'brand-awareness': { 'Social Ads': 25, 'SEO': 25, 'Content Marketing': 15, 'Events/PR': 15, 'Paid Search': 10, 'Email Marketing': 5, 'Influencer/Partnerships': 5 },
    'lead-generation': { 'Paid Search': 30, 'SEO': 25, 'Social Ads': 15, 'Email Marketing': 15, 'Content Marketing': 10, 'Events/PR': 5, 'Influencer/Partnerships': 0 },
    'sales-revenue': { 'Paid Search': 30, 'SEO': 20, 'Social Ads': 20, 'Email Marketing': 15, 'Content Marketing': 5, 'Events/PR': 5, 'Influencer/Partnerships': 5 },
    'customer-retention': { 'Email Marketing': 30, 'Social Ads': 20, 'SEO': 15, 'Content Marketing': 15, 'Events/PR': 10, 'Paid Search': 5, 'Influencer/Partnerships': 5 },
  },
};

function getWidgetDescription(slug: string, result: IToolResult): string {
  const budgetFormatted = result.totalBudget.toLocaleString();

  switch (slug) {
    case 'charts':
      return `Visualize how your $${budgetFormatted} budget is split across channels. Charts make it easy to present your marketing spend to stakeholders and track allocation changes over time.`;
    case 'pricing-tables':
      return `Present your marketing packages and service tiers with professional pricing tables. Perfect for agencies managing budgets across multiple clients.`;
    case 'popup-builder':
      return `Capture more leads from your website traffic without increasing your ad budget. Popups help you convert existing visitors into subscribers and customers.`;
    case 'countdown':
      return `Create urgency for your marketing campaigns with countdown timers. Drive faster action on promotions, product launches, and limited-time offers.`;
    default:
      return `Get more from your $${budgetFormatted} marketing budget with the right website widgets.`;
  }
}

function getWidgetCTA(slug: string): string {
  switch (slug) {
    case 'charts':
      return 'Add Charts Free →';
    case 'pricing-tables':
      return 'Add Pricing Tables Free →';
    case 'popup-builder':
      return 'Add Popup Builder Free →';
    case 'countdown':
      return 'Add Countdown Free →';
    default:
      return 'Try It Free →';
  }
}

export const MarketingBudgetTool = ({ recommendedWidgets, relatedTools }: IProps) => {
  const [budget, setBudget] = useState('');
  const [businessType, setBusinessType] = useState<BusinessType>('b2b-saas');
  const [goal, setGoal] = useState<GoalType>('lead-generation');
  const [result, setResult] = useState<IToolResult | null>(null);
  const [hasCalculated, setHasCalculated] = useState(false);
  const [error, setError] = useState('');
  const resultRef = useRef<HTMLDivElement>(null);

  const handleCalculate = () => {
    const budgetVal = parseFloat(budget);

    if (isNaN(budgetVal) || budgetVal <= 0) {
      setError('Please enter a valid monthly budget greater than zero.');
      return;
    }

    setError('');

    const allocationMap = ALLOCATION_MAP[businessType][goal];
    const allocations: IChannelAllocation[] = Object.entries(allocationMap)
      .filter(([, pct]) => pct > 0)
      .sort(([, a], [, b]) => b - a)
      .map(([channel, pct]) => ({
        channel,
        percentage: pct,
        amount: Math.round((budgetVal * pct) / 100),
      }));

    setResult({
      totalBudget: budgetVal,
      businessType,
      goal,
      allocations,
      status: 'good',
    });
    setHasCalculated(true);
    setTimeout(() => {
      resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const businessTypeLabels: Record<BusinessType, string> = {
    'b2b-saas': 'B2B SaaS',
    'b2c-ecommerce': 'B2C E-Commerce',
    'b2b-services': 'B2B Services',
    'd2c-brand': 'D2C Brand',
    'local-business': 'Local Business',
  };

  const goalLabels: Record<GoalType, string> = {
    'brand-awareness': 'Brand Awareness',
    'lead-generation': 'Lead Generation',
    'sales-revenue': 'Sales/Revenue',
    'customer-retention': 'Customer Retention',
  };

  return (
    <>
      <StyledToolHero>
        <h1>Free <span className="highlight">Marketing Budget</span> Allocator</h1>
        <p>
          Get a data-backed budget breakdown for your business type and goals.
          Free, instant, no sign-up required.
        </p>
      </StyledToolHero>

      <StyledToolBody>
        <StyledCalculator>
          <h2>Allocate Your Marketing Budget</h2>

          <StyledFormGroup>
            <label htmlFor="budget">Total Monthly Budget ($)</label>
            <input
              id="budget"
              type="number"
              min="0"
              step="100"
              placeholder="e.g. 10000"
              value={budget}
              onChange={(e) => setBudget(e.target.value)}
            />
            <p className="hint">Your total marketing spend per month in USD</p>
          </StyledFormGroup>

          <StyledFieldsGrid>
            <StyledFormGroup>
              <label htmlFor="businessType">Business Type</label>
              <select
                id="businessType"
                value={businessType}
                onChange={(e) => setBusinessType(e.target.value as BusinessType)}
              >
                <option value="b2b-saas">B2B SaaS</option>
                <option value="b2c-ecommerce">B2C E-Commerce</option>
                <option value="b2b-services">B2B Services</option>
                <option value="d2c-brand">D2C Brand</option>
                <option value="local-business">Local Business</option>
              </select>
            </StyledFormGroup>

            <StyledFormGroup>
              <label htmlFor="goal">Primary Goal</label>
              <select
                id="goal"
                value={goal}
                onChange={(e) => setGoal(e.target.value as GoalType)}
              >
                <option value="brand-awareness">Brand Awareness</option>
                <option value="lead-generation">Lead Generation</option>
                <option value="sales-revenue">Sales/Revenue</option>
                <option value="customer-retention">Customer Retention</option>
              </select>
            </StyledFormGroup>
          </StyledFieldsGrid>

          {error && (
            <p style={{ color: '#e22029', fontSize: '0.875rem', marginBottom: '16px' }}>{error}</p>
          )}

          <StyledCalculateButton onClick={handleCalculate}>
            Get My Budget Breakdown
          </StyledCalculateButton>
        </StyledCalculator>

        {hasCalculated && result && (
          <>
            <StyledResult status={result.status} ref={resultRef}>
              <p className="result-label">Recommended Budget Allocation</p>
              <p className="result-number">
                ${result.totalBudget.toLocaleString()}
              </p>
              <span className="result-badge">Allocation Ready</span>

              <div className="result-benchmark">
                <p>
                  <strong>Business Type:</strong> {businessTypeLabels[result.businessType as BusinessType]} &bull; <strong>Goal:</strong> {goalLabels[result.goal as GoalType]}
                </p>
              </div>

              <StyledAllocationGrid>
                {result.allocations.map((alloc) => (
                  <StyledAllocationCard key={alloc.channel}>
                    <div className="channel-name">{alloc.channel}</div>
                    <div className="channel-amount">${alloc.amount.toLocaleString()}</div>
                    <div className="channel-pct">{alloc.percentage}% of total budget</div>
                  </StyledAllocationCard>
                ))}
              </StyledAllocationGrid>

              <div className="result-pain">
                <strong>How to use this breakdown:</strong> These allocations are based on industry benchmarks for {businessTypeLabels[result.businessType as BusinessType]} businesses focused on {goalLabels[result.goal as GoalType].toLowerCase()}. Start with this split, then adjust based on your actual performance data. Track ROAS and CAC for each channel monthly, and shift budget toward the channels delivering the best results.
              </div>
            </StyledResult>

            <StyledWidgetsSection>
              <h2>Get More From Your Marketing Budget</h2>
              <p className="subtitle">
                These widgets help you visualize spend, capture leads, and drive conversions without increasing your budget.
              </p>
              <StyledWidgetGrid>
                {recommendedWidgets.map((widget) => (
                  <StyledWidgetCard
                    key={widget._id}
                    href={`/widgets/${widget.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <div className="widget-header">
                      {widget.logo && (
                        <Image
                          src={widget.logo}
                          alt={`${widget.name} logo`}
                          width={48}
                          height={48}
                        />
                      )}
                      <h3>{widget.name}</h3>
                    </div>
                    <p>{getWidgetDescription(widget.slug, result)}</p>
                    <span className="widget-cta">{getWidgetCTA(widget.slug)}</span>
                  </StyledWidgetCard>
                ))}
              </StyledWidgetGrid>
            </StyledWidgetsSection>
          </>
        )}

        {relatedTools.length > 0 && (
          <StyledRelatedTools>
            <h2>Explore More Free Tools</h2>
            <p className="subtitle">
              Other free calculators to help you measure and optimize your business.
            </p>
            <StyledRelatedToolGrid>
              {relatedTools.map((tool) => (
                <StyledRelatedToolCard
                  key={tool.slug}
                  as={Link}
                  href={`/free-tools/business/${tool.slug}`}
                >
                  <span className="tool-icon">{TOOL_ICONS[tool.slug] || <HiOutlineChartBar />}</span>
                  <h3>{tool.name}</h3>
                  <p>{tool.description}</p>
                  <span className="tool-cta">Use Tool {'→'}</span>
                </StyledRelatedToolCard>
              ))}
            </StyledRelatedToolGrid>
          </StyledRelatedTools>
        )}

      </StyledToolBody>
    </>
  );
};
